import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronRight, ChevronDown, Bookmark, Mail, Download, Check, X, Loader2,
  AlertCircle, AlertTriangle, ExternalLink, RefreshCw, Sparkles, ArrowUpRight,
} from 'lucide-react';
import CompanyLogo from './CompanyLogo';
import PageNav from './PageNav';
import { isWatchlisted, recordKey, stripMarkdown, formatShortDate } from '../utils/format';
import { getRiskLevel } from '../utils/risk';
import { displayHeadline } from '../utils/displayHeadline';
import { formatSourceLabel, isVerifiedSource } from '../constants/trustedSources';
import { sendAlertBrief } from '../utils/emailActivity';

const SEVERITY_PILL = {
  high: { label: 'High', cls: 'text-[#bd2f2b] bg-[#fae8e7]' },
  medium: { label: 'Medium', cls: 'text-[#86690c] bg-[#f7f0d2]' },
  low: { label: 'Low', cls: 'text-[#117049] bg-[#e1f3ea]' },
  unknown: { label: 'Unrated', cls: 'text-[#4b6679] bg-[#e6edf2]' },
};

// Generic guidance per category; shown as a prompt for the reader, not as a forecast.
const NEXT_STEPS = {
  legal: 'Watch for a court listing, a formal response from the company, and any related filings.',
  regulatory: 'Watch for the regulator’s next notice, the company’s response and any change in operating status.',
  financial: 'Watch for the company’s disclosures, lender or auditor statements and any restatement.',
  criminal: 'Watch for formal charges or a court record. An investigation is not a finding of fault.',
  hr: 'Watch for an official company statement and any regulatory or labour-board follow-up.',
  compliance: 'Watch for the company’s remediation statement and any follow-up from the authority.',
};

const BTN = 'inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-md border border-[#cbd3df] bg-white text-[13px] font-medium text-[#0d1524] whitespace-nowrap hover:bg-[#f6f7fa] disabled:opacity-40 disabled:cursor-not-allowed transition';
const CARD = 'bg-white border border-[#e1e5ec] rounded-lg';

function Pill({ severity }) {
  const style = SEVERITY_PILL[getRiskLevel(severity)] || SEVERITY_PILL.unknown;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs leading-none font-semibold px-2 py-1.5 rounded whitespace-nowrap ${style.cls}`}>
      <i className="w-1.5 h-1.5 rounded-full bg-current" />
      {style.label}
    </span>
  );
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

function timeOf(record) {
  const t = record?.published_at ? new Date(record.published_at) : null;
  return t && !Number.isNaN(t.getTime()) ? t : null;
}

function formatDateTime(record) {
  const t = timeOf(record);
  if (!t) return 'Date unknown';
  return t.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function AlertDetailPage({
  record,
  related = [],
  sequence = [],
  records = [],
  watchlist,
  toggleWatchlist,
  summarizingId,
  summarizeRecord,
  onBack,
  backLabel = 'All stories',
  onNavigate,
  onAskAi,
  onViewCompany,
  onEmailSent,
}) {
  const [briefState, setBriefState] = useState('idle');
  const [menuOpen, setMenuOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const menuRef = useRef(null);

  const key = recordKey(record);
  const saved = isWatchlisted(record, watchlist || []);
  const verified = isVerifiedSource(record.source_domain);
  const level = getRiskLevel(record.severity);
  const category = record.categories?.[0] || '';
  const summary = stripMarkdown(record.summary || record.snippet || record.content || '');
  const summarizing = summarizingId === record.id;

  const sources = useMemo(() => {
    const seen = new Set();
    return [record, ...related].filter(r => {
      const k = recordKey(r) || `${r.source_domain}|${r.url}|${r.title}`;
      if (!k || seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [record, related]);

  const seqIndex = sequence.findIndex(r => recordKey(r) === key);
  const prev = seqIndex > 0 ? sequence[seqIndex - 1] : null;
  const next = seqIndex >= 0 && seqIndex < sequence.length - 1 ? sequence[seqIndex + 1] : null;

  const companyAlerts = useMemo(
    () => records.filter(r => r.company === record.company),
    [records, record.company],
  );
  const companySaved = companyAlerts.filter(r => isWatchlisted(r, watchlist || [])).length;
  const companyHigh = companyAlerts.filter(r => getRiskLevel(r.severity) === 'high').length;

  const timeline = useMemo(() => sources
    .map(r => ({ r, t: timeOf(r) }))
    .sort((a, b) => (b.t?.getTime() || 0) - (a.t?.getTime() || 0)), [sources]);

  useEffect(() => {
    setBriefState('idle');
    setMenuOpen(false);
    window.scrollTo(0, 0);
  }, [key]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.closest?.('input, textarea, select')) return;
      if (e.key === 'Escape') onBack();
      if (e.key === 'ArrowLeft' && prev) onNavigate(prev);
      if (e.key === 'ArrowRight' && next) onNavigate(next);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onBack, onNavigate, prev, next]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  const handleSendBrief = async () => {
    if (briefState === 'sending') return;
    setBriefState('sending');
    try {
      const activity = await sendAlertBrief(record);
      setBriefState(activity.status === 'failed' ? 'failed' : 'sent');
      onEmailSent?.(activity);
    } catch {
      setBriefState('failed');
    }
    setTimeout(() => setBriefState('idle'), 2500);
  };

  const copyBrief = async () => {
    const text = [
      displayHeadline(record),
      `${record.company}${category ? ` · ${category}` : ''} · ${formatDateTime(record)}`,
      '',
      summary,
      '',
      ...sources.map((s, i) => `[${i + 1}] ${formatSourceLabel(s.source_domain) || 'Source'}${s.url ? ` — ${s.url}` : ''}`),
    ].join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable */ }
    setMenuOpen(false);
  };

  const whyText = category
    ? `Priority comes from the ${category} category and the keywords in the headline.`
    : 'Priority comes from the keywords in the headline.';

  const askAbout = (question) => onAskAi?.(question);

  return (
    <div>
      <PageNav
        backLabel={backLabel}
        onBack={onBack}
        crumbs={[record.company, displayHeadline(record)]}
        pager={{
          index: seqIndex,
          total: sequence.length,
          noun: 'in your list',
          onPrev: prev ? () => onNavigate(prev) : undefined,
          onNext: next ? () => onNavigate(next) : undefined,
          prevTitle: prev ? displayHeadline(prev) : '',
          nextTitle: next ? displayHeadline(next) : '',
        }}
      />

      {/* Actions */}
      <div className="flex flex-wrap justify-end gap-2 mb-3.5">
        <button type="button" onClick={() => toggleWatchlist(key)}
          className={`${BTN} ${saved ? '!bg-[#e7edfc] !border-[#1c4ed8] !text-[#1c4ed8]' : ''}`}>
          <Bookmark size={14} fill={saved ? 'currentColor' : 'none'} />{saved ? 'Saved' : 'Save'}
        </button>
        <button type="button" onClick={handleSendBrief} disabled={briefState === 'sending'} className={BTN}>
          {briefState === 'sending' ? <><Loader2 size={14} className="animate-spin" />Sending…</>
            : briefState === 'sent' ? <><Check size={14} className="text-[#117049]" />Brief sent</>
              : briefState === 'failed' ? <><AlertCircle size={14} className="text-[#bd2f2b]" />Failed</>
                : <><Mail size={14} />Email brief</>}
        </button>
        <div className="relative" ref={menuRef}>
          <button type="button" onClick={() => setMenuOpen(o => !o)} aria-haspopup="menu" aria-expanded={menuOpen} className={BTN}>
            <Download size={14} />Export<ChevronDown size={12} />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-[calc(100%+6px)] min-w-[220px] bg-white border border-[#cbd3df] rounded-lg shadow-lg p-1 z-50">
              <button type="button" onClick={() => { setMenuOpen(false); window.print(); }}
                className="w-full text-left rounded px-2.5 py-2 text-[13px] hover:bg-[#f6f7fa]">Print or save as PDF</button>
              <button type="button" onClick={copyBrief}
                className="w-full text-left rounded px-2.5 py-2 text-[13px] hover:bg-[#f6f7fa]">
                {copied ? 'Copied' : 'Copy as text brief'}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="grid gap-4 items-start grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px]">
        {/* Article */}
        <article className={`${CARD} min-w-0 overflow-hidden`}>
          <div className="px-7 pt-6 pb-6">
            <div className="flex flex-wrap items-center justify-between gap-2.5">
              <span className="flex items-center gap-2">
                <Pill severity={record.severity} />
                {category && (
                  <span className="text-xs font-medium px-2 py-1.5 leading-none rounded border border-[#e1e5ec] bg-[#f6f7fa] text-[#45516a] capitalize">
                    {category}
                  </span>
                )}
              </span>
              <span className="text-[13px] text-[#5b6780]">Published {formatDateTime(record)}</span>
            </div>

            <div className="flex items-center gap-2.5 mt-5">
              <CompanyLogo name={record.company} size="md" />
              <span className="text-[15px] font-semibold text-[#1c4ed8]">{record.company}</span>
              <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-[#f6f7fa] border border-[#e1e5ec] text-[#45516a]">
                {record.entity_type === 'person' ? 'Person' : 'Company'}
              </span>
            </div>

            <h1 className="mt-3 text-[28px] leading-[1.2] font-semibold tracking-[-0.015em] text-[#0d1524] [text-wrap:balance]">
              {displayHeadline(record)}
            </h1>

            {!verified && (
              <div role="note" className="flex gap-2.5 items-start bg-[#fbeddb] rounded-md px-3.5 py-3 text-[13px] mt-4">
                <AlertTriangle size={16} className="text-[#b05a0a] mt-px flex-shrink-0" />
                <span>
                  <b className="font-semibold">Unconfirmed report.</b> This comes from a source we could not verify.
                  Do not forward it as fact.{' '}
                  <button type="button" className="text-[#1c4ed8] font-medium hover:underline"
                    onClick={() => askAbout(`Is there a second source for: ${displayHeadline(record)}`)}>
                    Look for corroboration
                  </button>
                </span>
              </div>
            )}

            <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-md border border-[#e1e5ec] bg-[#f6f7fa] px-4 py-3">
              <div className="min-w-0">
                <b className="block text-sm font-semibold text-[#0d1524]">{formatSourceLabel(record.source_domain) || 'Source'}</b>
                <span className={`mt-0.5 inline-flex items-center gap-1 text-xs font-medium ${verified ? 'text-[#117049]' : 'text-[#b05a0a]'}`}>
                  {verified ? <Check size={12} /> : <X size={12} />}
                  {verified ? 'Verified source · identity checked, not a finding of fault' : 'Unverified source · not corroborated'}
                </span>
              </div>
              {record.url ? (
                <a href={record.url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-[#cbd3df] bg-white text-[13px] font-medium text-[#0d1524] hover:bg-[#f6f7fa] transition">
                  <ExternalLink size={13} />Open original article
                </a>
              ) : (
                <span className="text-[13px] text-[#76829a]">No article link</span>
              )}
            </div>
          </div>

          <div className="px-7 py-6 border-t border-[#e1e5ec]">
            <SectionHeading>The development</SectionHeading>
            <p className="mt-2.5 max-w-[78ch] text-[14.5px] leading-[1.7] text-[#45516a]">
              {summary || 'No summary has been generated for this story yet.'} <CiteLink n={1} />
            </p>
            <button
              type="button"
              onClick={() => summarizeRecord?.(record.id)}
              disabled={summarizing || !summarizeRecord}
              className="mt-3 inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border border-[#e1e5ec] text-xs font-medium text-[#45516a] hover:bg-[#f6f7fa] disabled:opacity-50 transition"
            >
              {summarizing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              {summarizing ? 'Generating summary…' : summary ? 'Refresh summary' : 'Generate summary'}
            </button>

            <div className="mt-7">
              <SectionHeading>What the reporting establishes</SectionHeading>
              <p className="mt-2.5 max-w-[78ch] text-[14.5px] leading-[1.7] text-[#45516a]">
                {verified
                  ? 'The source is verified and the item is attributed to its publisher.'
                  : 'The source is not verified. Treat this item as unconfirmed until a second source reports it.'}
                {' '}{sources.length > 1
                  ? `It is covered by ${sources.length} sources, listed below.`
                  : `${record.company} has not published a statement in the monitored sources.`}{' '}
                <CiteLink n={1} />
              </p>
            </div>

            <div className="mt-7 max-w-[78ch] bg-[#e7edfc] border-l-[3px] border-[#1c4ed8] rounded-r-md px-4 py-3.5">
              <b className="block mb-1 text-[13px] font-semibold text-[#1c4ed8]">What to follow next</b>
              <p className="text-[13.5px] leading-relaxed text-[#0d1524]">
                {NEXT_STEPS[String(category).toLowerCase()] || 'Watch for a company statement and any follow-up reporting.'}{' '}
                {SEVERITY_PILL[level]?.label || 'This'} is an attention-priority label, not a forecast.
              </p>
            </div>

            <details className="group mt-3 max-w-[78ch] rounded-md border border-[#e1e5ec]">
              <summary className="flex items-center justify-between cursor-pointer list-none px-4 py-2.5 text-[13px] font-medium text-[#0d1524] hover:bg-[#f6f7fa] rounded-md">
                Why is this {SEVERITY_PILL[level]?.label || 'rated'}?
                <ChevronDown size={14} className="text-[#76829a] transition group-open:rotate-180" />
              </summary>
              <p className="px-4 pb-3 pt-1 text-[13px] leading-relaxed text-[#45516a]">{whyText}</p>
            </details>
          </div>

          <div className="px-7 py-6 border-t border-[#e1e5ec]">
            <SectionHeading>
              Sources &amp; related reporting
              <span className="ml-1 text-xs font-medium px-1.5 py-0.5 rounded bg-[#f6f7fa] border border-[#e1e5ec] text-[#45516a]">{pad2(sources.length)}</span>
            </SectionHeading>
            <div className="mt-3 rounded-md border border-[#e1e5ec] overflow-hidden">
              {sources.map((s, i) => (
                <div id={`src${i + 1}`} key={recordKey(s) || i}
                  className="grid grid-cols-[32px_minmax(0,1fr)_auto_20px] items-center gap-3 px-3.5 py-3 border-t border-[#e1e5ec] first:border-t-0 text-[13px] target:bg-[#e7edfc]">
                  <span className="w-6 h-6 grid place-items-center rounded bg-[#e7edfc] text-xs font-semibold text-[#1c4ed8]">{i + 1}</span>
                  <span className="min-w-0">
                    <b className="block font-medium text-[#0d1524] line-clamp-1">{displayHeadline(s)}</b>
                    <small className="text-xs text-[#76829a]">
                      {formatSourceLabel(s.source_domain) || 'Source'}
                      {s.published_at ? ` · ${formatShortDate(s.published_at)}` : ''}
                    </small>
                  </span>
                  <span className="text-xs font-medium text-[#45516a] bg-[#f6f7fa] border border-[#e1e5ec] rounded px-1.5 py-0.5">
                    {i === 0 ? (isVerifiedSource(s.source_domain) ? 'Primary source' : 'Unverified report') : 'Related'}
                  </span>
                  {s.url ? (
                    <a href={s.url} target="_blank" rel="noopener noreferrer" aria-label="Open article" className="text-[#76829a] hover:text-[#1c4ed8]">
                      <ArrowUpRight size={15} />
                    </a>
                  ) : <span />}
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs text-[#76829a]">
              Editorial summary. If the original article is later removed or edited, this summary and its source record stay available.
            </p>
          </div>
        </article>

        {/* Right column */}
        <div className="flex flex-col gap-4 min-w-0">
          <div className={`${CARD} p-[18px]`}>
            <span className="text-sm font-semibold text-[#0d1524]">Entity in focus</span>
            <div className="flex items-center gap-3 mt-3.5">
              <CompanyLogo name={record.company} size="lg" />
              <div className="min-w-0">
                <h3 className="text-[17px] font-semibold leading-tight text-[#0d1524] truncate">{record.company}</h3>
                <span className="mt-1 inline-block text-xs font-medium px-1.5 py-0.5 rounded bg-[#f6f7fa] border border-[#e1e5ec] text-[#45516a]">
                  {record.entity_type === 'person' ? 'Person' : 'Company'}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 mt-4">
              {[
                ['Stories', companyAlerts.length, ''],
                ['High risk', companyHigh, companyHigh ? 'text-[#bd2f2b]' : ''],
                ['Saved', companySaved, ''],
              ].map(([label, n, tone]) => (
                <div key={label} className="rounded-md bg-[#f6f7fa] border border-[#e1e5ec] px-3 py-2.5">
                  <b className={`block text-xl font-semibold tracking-[-0.02em] ${tone}`}>{pad2(n)}</b>
                  <small className="text-xs text-[#5b6780]">{label}</small>
                </div>
              ))}
            </div>
            <button type="button" onClick={() => onViewCompany?.(record.company)}
              className="mt-3 w-full inline-flex items-center justify-center gap-1.5 h-8 rounded-md border border-[#cbd3df] bg-white text-[13px] font-medium text-[#0d1524] hover:bg-[#f6f7fa] transition">
              View all {record.company} stories<ChevronRight size={14} />
            </button>
          </div>

          <div className={`${CARD} p-[18px]`}>
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-[#0d1524]">Story timeline</h3>
              <span className="text-xs text-[#76829a]">{timeline.length} {timeline.length === 1 ? 'event' : 'events'}</span>
            </div>
            <ul className="list-none mt-3.5 ml-0 pl-3.5 border-l border-[#cbd3df]">
              {timeline.map(({ r, t }, i) => (
                <li key={recordKey(r) || i} className="relative pl-3 pb-4 last:pb-0">
                  <span className={`absolute -left-[18px] top-[5px] w-[7px] h-[7px] rounded-full border-2 ${
                    i === 0 ? 'bg-[#1c4ed8] border-[#1c4ed8]' : 'bg-white border-[#cbd3df]'
                  }`} />
                  <span className="text-xs text-[#76829a]">
                    {t ? t.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'Date unknown'}
                  </span>
                  <b className="block my-0.5 text-[13px] font-medium text-[#0d1524]">Published by {formatSourceLabel(r.source_domain) || 'source'}</b>
                  {i === 0 && timeline.length > 1 && <small className="text-xs text-[#45516a]">Latest report</small>}
                </li>
              ))}
            </ul>
          </div>

          <div className={`${CARD} p-[18px]`}>
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-[#0d1524]">Ask AI about this story</h3>
              <Sparkles size={15} className="text-[#1c4ed8]" />
            </div>
            <p className="mt-1 text-xs text-[#76829a]">Answers use your monitored coverage, with sources.</p>
            <div className="mt-2.5">
              {[
                'Summarize this story in 3 bullets',
                'What are the open questions?',
                'Is there a second source for this?',
                `How could this affect ${record.company}?`,
              ].map(prompt => (
                <button
                  key={prompt}
                  type="button"
                  onClick={() => askAbout(`${prompt} (${displayHeadline(record)})`)}
                  className="flex w-full items-center justify-between gap-2 py-2 border-t border-[#e1e5ec] text-left text-[13px] text-[#0d1524] hover:text-[#1c4ed8] transition"
                >
                  {prompt}
                  <ChevronRight size={14} className="flex-none text-[#76829a]" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionHeading({ children }) {
  return (
    <h3 className="flex items-center gap-2 text-[15px] font-semibold text-[#0d1524]">
      <span className="w-1 h-4 rounded-full bg-[#1c4ed8]" />
      {children}
    </h3>
  );
}

function CiteLink({ n }) {
  return (
    <a href={`#src${n}`} className="text-[12px] font-medium text-[#1c4ed8] hover:underline" style={{ fontFamily: 'inherit' }}>
      [{n}]
    </a>
  );
}

export default AlertDetailPage;
